import { useEffect, useState, useCallback } from "react";
import { useApp } from "@/context/AppContext";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";
import StatementDoc from "@/components/StatementDoc";
import {
  ScrollText, Save, Camera, Loader2, Printer, FileDown, Link2, Mail, Copy, Trash2, X, Send, Layers,
} from "lucide-react";

const emptyProfile = { holder_name: "", holder_address: "", holder_account_no: "", info_email: "", header_url: "", footer_note: "" };
const inp = "w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:ring-2 focus:ring-indigo-500/20 outline-none";
const fileUrl = (u) => (u ? `${process.env.REACT_APP_BACKEND_URL}${u}` : null);

export default function Statement() {
  const { companyId, company, loadCompanies } = useApp();
  const { hasPermission } = useAuth();
  const canManage = hasPermission("manage_company");

  const [profile, setProfile] = useState(emptyProfile);
  const [accounts, setAccounts] = useState([]);
  const [ctrl, setCtrl] = useState({ account_id: "", start: "", end: "", statement_date: new Date().toISOString().slice(0, 10) });
  const [data, setData] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [links, setLinks] = useState([]);
  const [emailFor, setEmailFor] = useState(null);
  const [emailTo, setEmailTo] = useState("");
  const [bulk, setBulk] = useState(null);
  const [bulkBusy, setBulkBusy] = useState(false);

  useEffect(() => { if (company) setProfile({ ...emptyProfile, ...(company.statement_profile || {}) }); }, [company]);
  useEffect(() => {
    if (!companyId) return;
    api.get(`/companies/${companyId}/accounts`).then(({ data }) => setAccounts(data)).catch(() => {});
    loadLinks();
  }, [companyId]);

  const loadLinks = () => api.get(`/companies/${companyId}/statements`).then(({ data }) => setLinks(data)).catch(() => {});

  const loadPreview = useCallback(async () => {
    if (!companyId) return;
    const params = {};
    Object.entries(ctrl).forEach(([k, v]) => { if (v) params[k] = v; });
    try {
      const { data } = await api.get(`/companies/${companyId}/statement`, { params });
      setData(data);
    } catch (e) { console.error("Statement preview load failed:", e); }
  }, [companyId, ctrl]);
  useEffect(() => { loadPreview(); }, [loadPreview]);

  const saveProfile = async () => {
    try {
      await api.put(`/companies/${companyId}/statement-profile`, profile);
      await loadCompanies();
      toast.success("Statement details saved");
      loadPreview();
    } catch { toast.error("Save failed"); }
  };

  const onHeader = async (e) => {
    const f = e.target.files[0]; if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const { data } = await api.post("/upload", fd);
      setProfile((p) => ({ ...p, header_url: data.url }));
      toast.success("Header image uploaded — remember to save");
    } catch { toast.error("Upload failed"); } finally { setUploading(false); e.target.value = ""; }
  };

  const downloadPdf = async () => {
    const params = new URLSearchParams();
    Object.entries(ctrl).forEach(([k, v]) => { if (v) params.append(k, v); });
    try {
      const res = await api.get(`/companies/${companyId}/statement/pdf?${params.toString()}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank");
    } catch { toast.error("PDF failed"); }
  };

  const createLink = async () => {
    try {
      const { data } = await api.post(`/companies/${companyId}/statements`, ctrl);
      toast.success("Share link created");
      loadLinks();
      navigator.clipboard?.writeText(data.url);
      toast.message("Link copied to clipboard");
    } catch { toast.error("Could not create link"); }
  };
  const revoke = async (id) => { await api.delete(`/companies/${companyId}/statements/${id}`); toast.success("Link revoked"); loadLinks(); };
  const sendEmail = async () => {
    try {
      await api.post(`/companies/${companyId}/statements/${emailFor}/email`, { to: emailTo });
      toast.success(`Statement sent to ${emailTo}`);
      setEmailFor(null); setEmailTo("");
    } catch (e) { toast.error(e.response?.data?.detail || "Email failed"); }
  };

  const openBulk = () => setBulk({ month: ctrl.statement_date.slice(0, 7), account_ids: accounts.map((a) => a.id), email_to: "" });
  const toggleBulkAcct = (id) => setBulk((b) => ({ ...b, account_ids: b.account_ids.includes(id) ? b.account_ids.filter((x) => x !== id) : [...b.account_ids, id] }));
  const runBulk = async () => {
    if (!bulk.account_ids.length) { toast.error("Select at least one account"); return; }
    setBulkBusy(true);
    try {
      const { data } = await api.post(`/companies/${companyId}/statements/bulk`, bulk);
      const emailed = data.statements.filter((s) => s.emailed).length;
      toast.success(`Generated ${data.count} statement(s)${bulk.email_to ? ` · emailed ${emailed}` : ""}`);
      setBulk(null); loadLinks();
    } catch (e) { toast.error(e.response?.data?.detail || "Bulk generation failed"); }
    finally { setBulkBusy(false); }
  };

  if (!companyId)
    return <div className="h-96 grid place-items-center text-slate-400">Create a company first.</div>;

  return (
    <div className="space-y-6 animate-fade-up" data-testid="statement-page">
      <div className="flex items-center justify-between flex-wrap gap-3 no-print">
        <div>
          <h1 className="text-2xl md:text-3xl font-heading font-extrabold text-slate-900 flex items-center gap-2"><ScrollText className="text-indigo-600" />Statement</h1>
          <p className="text-slate-500 text-sm mt-0.5">Generate a formal, shareable account statement</p>
        </div>
        <div className="flex gap-2">
          <button data-testid="print-statement-btn" onClick={() => window.print()} className="px-3 py-2.5 rounded-xl border border-slate-200 bg-white font-semibold text-sm flex items-center gap-2 hover:bg-slate-50"><Printer size={16} /> Print</button>
          <button data-testid="pdf-statement-btn" onClick={downloadPdf} className="px-3 py-2.5 rounded-xl border border-slate-200 bg-white font-semibold text-sm flex items-center gap-2 hover:bg-slate-50"><FileDown size={16} /> PDF</button>
          <button data-testid="bulk-statement-btn" onClick={openBulk} className="px-3 py-2.5 rounded-xl border border-slate-200 bg-white font-semibold text-sm flex items-center gap-2 hover:bg-slate-50"><Layers size={16} /> Bulk</button>
          <button data-testid="share-statement-btn" onClick={createLink} className="px-3 py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white font-semibold text-sm flex items-center gap-2"><Link2 size={16} /> Create Share Link</button>
        </div>
      </div>

      {/* Editable details */}
      {canManage && (
        <div className="bg-white rounded-2xl border border-slate-200 p-5 no-print">
          <div className="flex items-center gap-4 mb-4">
            <div className="relative">
              <div className="w-16 h-12 rounded-lg bg-slate-100 grid place-items-center overflow-hidden border border-slate-200">
                {profile.header_url ? <img src={fileUrl(profile.header_url)} alt="header" className="w-full h-full object-contain" data-testid="header-preview" /> : <span className="text-[10px] text-slate-400">Header</span>}
              </div>
              <label className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full bg-white border border-slate-200 grid place-items-center cursor-pointer shadow-sm">
                <input type="file" accept="image/*" className="hidden" data-testid="header-input" onChange={onHeader} />
                {uploading ? <Loader2 size={12} className="animate-spin text-indigo-600" /> : <Camera size={12} className="text-indigo-600" />}
              </label>
            </div>
            <p className="text-sm text-slate-500">Header image appears on the printed / shared statement.</p>
          </div>
          <div className="grid md:grid-cols-2 gap-3">
            <Fld label="Account holder name"><input data-testid="holder-name" className={inp} value={profile.holder_name} onChange={(e) => setProfile({ ...profile, holder_name: e.target.value })} /></Fld>
            <Fld label="Account number"><input data-testid="holder-acct" className={inp} value={profile.holder_account_no} onChange={(e) => setProfile({ ...profile, holder_account_no: e.target.value })} /></Fld>
            <div className="md:col-span-2"><Fld label="Holder address"><input className={inp} value={profile.holder_address} onChange={(e) => setProfile({ ...profile, holder_address: e.target.value })} /></Fld></div>
            <Fld label="Info email (shown on statement)"><input data-testid="info-email" className={inp} placeholder="info@yourcompany.com" value={profile.info_email} onChange={(e) => setProfile({ ...profile, info_email: e.target.value })} /></Fld>
            <Fld label="Footer note"><input className={inp} value={profile.footer_note} onChange={(e) => setProfile({ ...profile, footer_note: e.target.value })} /></Fld>
          </div>
          <div className="flex justify-end mt-4">
            <button data-testid="save-statement-profile" onClick={saveProfile} className="bg-indigo-600 hover:bg-indigo-700 text-white font-semibold px-5 py-2.5 rounded-xl flex items-center gap-2"><Save size={16} /> Save details</button>
          </div>
        </div>
      )}

      {/* Controls */}
      <div className="bg-white rounded-2xl border border-slate-200 p-4 flex flex-wrap items-end gap-3 no-print">
        <Fld label="Account"><select data-testid="stmt-account" className={inp} value={ctrl.account_id} onChange={(e) => setCtrl({ ...ctrl, account_id: e.target.value })}><option value="">All accounts</option>{accounts.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}</select></Fld>
        <Fld label="From"><input type="date" className={inp} value={ctrl.start} onChange={(e) => setCtrl({ ...ctrl, start: e.target.value })} /></Fld>
        <Fld label="To"><input type="date" className={inp} value={ctrl.end} onChange={(e) => setCtrl({ ...ctrl, end: e.target.value })} /></Fld>
        <Fld label="Statement date"><input type="date" data-testid="stmt-date" className={inp} value={ctrl.statement_date} onChange={(e) => setCtrl({ ...ctrl, statement_date: e.target.value })} /></Fld>
      </div>

      {/* Existing share links */}
      {links.length > 0 && (
        <div className="bg-white rounded-2xl border border-slate-200 p-4 space-y-2 no-print">
          <p className="text-sm font-semibold text-slate-700">Active share links</p>
          {links.map((l) => (
            <div key={l.id} className="flex items-center gap-2 text-sm" data-testid={`share-link-${l.id}`}>
              <Link2 size={14} className="text-slate-400" />
              <span className="text-xs font-semibold text-indigo-600 whitespace-nowrap">{l.statement_number}</span>
              <input readOnly value={l.url} className="flex-1 px-2 py-1.5 rounded-lg border border-slate-200 text-xs text-slate-600 bg-slate-50" />
              <button onClick={() => { navigator.clipboard?.writeText(l.url); toast.success("Copied"); }} className="p-1.5 text-slate-400 hover:text-indigo-600"><Copy size={15} /></button>
              <button data-testid={`email-link-${l.id}`} onClick={() => setEmailFor(l.id)} className="p-1.5 text-slate-400 hover:text-indigo-600"><Mail size={15} /></button>
              <button onClick={() => revoke(l.id)} className="p-1.5 text-slate-400 hover:text-rose-600"><Trash2 size={15} /></button>
            </div>
          ))}
        </div>
      )}

      {/* Preview */}
      <div className="rounded-2xl border border-slate-200 overflow-hidden shadow-sm print-area">
        <StatementDoc data={data} />
      </div>

      {bulk && (
        <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4 no-print" onClick={() => setBulk(null)}>
          <div className="bg-white rounded-2xl w-full max-w-md p-5 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="bulk-dialog">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading font-bold text-lg">Bulk statements</h3>
              <button onClick={() => setBulk(null)}><X size={20} className="text-slate-400" /></button>
            </div>
            <Fld label="Month"><input type="month" data-testid="bulk-month" className={inp} value={bulk.month} onChange={(e) => setBulk({ ...bulk, month: e.target.value })} /></Fld>
            <p className="text-xs font-semibold text-slate-600 mt-3 mb-1">Accounts ({bulk.account_ids.length} selected)</p>
            <div className="space-y-1.5 max-h-40 overflow-y-auto">
              {accounts.map((a) => (
                <label key={a.id} className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                  <input type="checkbox" className="accent-indigo-600" data-testid={`bulk-acct-${a.id}`} checked={bulk.account_ids.includes(a.id)} onChange={() => toggleBulkAcct(a.id)} />
                  {a.name} <span className="text-slate-400 text-xs">{a.bank_name}</span>
                </label>
              ))}
              {accounts.length === 0 && <p className="text-xs text-slate-400">No accounts yet — add one first.</p>}
            </div>
            <div className="mt-3"><Fld label="Email each to (optional)"><input type="email" data-testid="bulk-email" className={inp} placeholder="accountant@example.com" value={bulk.email_to} onChange={(e) => setBulk({ ...bulk, email_to: e.target.value })} /></Fld></div>
            <p className="text-[11px] text-slate-400 mt-1">Recipients get a secure link to view &amp; download each statement PDF.</p>
            <button data-testid="run-bulk-btn" onClick={runBulk} disabled={bulkBusy} className="w-full mt-4 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white font-semibold py-2.5 rounded-xl flex items-center justify-center gap-2">
              {bulkBusy ? <Loader2 size={16} className="animate-spin" /> : <Layers size={16} />} Generate {bulk.account_ids.length || ""} statement(s)
            </button>
          </div>
        </div>
      )}

      {emailFor && (
        <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4 no-print" onClick={() => setEmailFor(null)}>
          <div className="bg-white rounded-2xl w-full max-w-md p-5" onClick={(e) => e.stopPropagation()} data-testid="email-dialog">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading font-bold text-lg">Email this statement</h3>
              <button onClick={() => setEmailFor(null)}><X size={20} className="text-slate-400" /></button>
            </div>
            <Fld label="Recipient email"><input data-testid="email-to" type="email" className={inp} placeholder="client@example.com" value={emailTo} onChange={(e) => setEmailTo(e.target.value)} /></Fld>
            <p className="text-xs text-slate-400 mt-2">They'll receive a secure link to view the statement online.</p>
            <button data-testid="send-email-btn" onClick={sendEmail} disabled={!emailTo} className="w-full mt-4 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white font-semibold py-2.5 rounded-xl flex items-center justify-center gap-2"><Send size={16} /> Send</button>
          </div>
        </div>
      )}
    </div>
  );
}

const Fld = ({ label, children }) => (<div className="flex-1 min-w-[140px]"><label className="block text-xs font-semibold text-slate-600 mb-1">{label}</label>{children}</div>);
